import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/16/solid";
import { cn } from "@moe/oss/utils/utils";
import { TagBox } from "@web/components/tag/TagBox";
import { TagInput } from "@web/components/tag/TagInput";
import { TagList } from "@web/components/tag/TagList";
import { useAppContext } from "@web/route-services/root/AppContext";
import { forwardRef, useState } from "react";

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  initialValue?: string;
  onSearch?: (input: string) => void;
  onInputChange?: (input: string) => void;
  searchOnBlur?: boolean;
  onClear?: (clearedInput: string) => void;
  placeholder?: string;
  className?: string;
  inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>;
}

const defaultPlaceholder = "You may use #hashtags...";
export const SearchBar = forwardRef<HTMLDivElement, Props>(
  (
    {
      initialValue,
      onSearch,
      onInputChange,
      searchOnBlur,
      inputRef,
      placeholder = defaultPlaceholder,
      className = "",
      onClear,
      ...rest
    },
    ref
  ) => {
    const [input, setInput] = useState(initialValue ?? "");
    const { isDesktopScreen } = useAppContext();

    const handleClear = () => {
      setInput("");
      onSearch?.("");
      onClear?.(input);
    };

    const showClearButton = input || !isDesktopScreen;

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onSearch?.(input);
      }
    };

    const handleBlur = () => {
      if (searchOnBlur) {
        onSearch?.(input);
      }
    };

    return (
      <div
        ref={ref}
        className={cn(
          "bg-input-primary border-line relative flex h-12 w-80 shrink-0 select-none items-center space-x-2 rounded-2xl border px-2 py-5",
          className
        )}
        {...rest}
      >
        <MagnifyingGlassIcon className="text-tx-secondary ml-2 size-6 shrink-0" />
        <TagBox
          value={input}
          onChange={(input) => {
            setInput(input);
            onInputChange?.(input);
          }}
          className="flex flex-col"
        >
          <TagInput
            placeholder={placeholder}
            className="h-full border-none bg-inherit"
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            ref={inputRef}
          />
          <TagList className="absolute left-0 top-full z-50 mt-1 w-full" />
        </TagBox>

        {showClearButton && (
          <button className="p-2" onClick={handleClear}>
            <XMarkIcon className="text-tx-secondary hover:text-tx-primary active:text-tx-primary size-5 shrink-0" />
          </button>
        )}
      </div>
    );
  }
);
